import { useEffect } from 'react'

/**
 * Webflow's tab panels, in React.
 *
 * One page uses them — the homepage, five tabs. That was the last thing
 * `webflow.js` was still needed for outside the nav.
 *
 * As with the nav, this drives the ported markup through the classes the
 * stylesheet already defines (`w--current` on the link, `w--tab-active` on the
 * pane) rather than replacing the DOM. It also adds the roles and keyboard
 * behaviour a tablist is supposed to have, which the capture had none of:
 * arrow keys, Home/End, and one tab stop for the whole list.
 */
export function useWebflowTabs(route: string) {
  useEffect(() => {
    const groups = [...document.querySelectorAll<HTMLElement>('.w-tabs')]
    if (!groups.length) return

    const cleanups: Array<() => void> = []

    for (const group of groups) {
      const links = [...group.querySelectorAll<HTMLElement>('.w-tab-link')]
      const panes = [...group.querySelectorAll<HTMLElement>('.w-tab-pane')]
      if (!links.length || !panes.length) continue

      const menu = links[0].parentElement
      menu?.setAttribute('role', 'tablist')

      const select = (i: number, focus = false) => {
        links.forEach((l, n) => {
          const on = n === i
          l.classList.toggle('w--current', on)
          l.setAttribute('aria-selected', String(on))
          l.setAttribute('tabindex', on ? '0' : '-1')
          if (on && focus) l.focus()
        })
        panes.forEach((pane, n) => {
          const on = n === i
          pane.classList.toggle('w--tab-active', on)
          // `display` is what the stylesheet keys the pane off; keep both in
          // step so a pane cannot be active and invisible.
          pane.style.display = on ? '' : 'none'
        })
      }

      links.forEach((link, i) => {
        link.setAttribute('role', 'tab')
        const pane = panes[i]
        if (pane) {
          pane.setAttribute('role', 'tabpanel')
          if (!pane.id) pane.id = `${group.id || 'tabs'}-pane-${i}`
          if (!link.id) link.id = `${group.id || 'tabs'}-tab-${i}`
          link.setAttribute('aria-controls', pane.id)
          pane.setAttribute('aria-labelledby', link.id)
        }

        const onClick = (e: Event) => {
          e.preventDefault()
          select(i)
        }
        const onKey = (e: Event) => {
          const ev = e as KeyboardEvent
          const last = links.length - 1
          const next =
            ev.key === 'ArrowRight' || ev.key === 'ArrowDown' ? (i === last ? 0 : i + 1)
            : ev.key === 'ArrowLeft' || ev.key === 'ArrowUp' ? (i === 0 ? last : i - 1)
            : ev.key === 'Home' ? 0
            : ev.key === 'End' ? last
            : null
          if (next === null) return
          ev.preventDefault()
          select(next, true)
        }
        link.addEventListener('click', onClick)
        link.addEventListener('keydown', onKey)
        cleanups.push(() => {
          link.removeEventListener('click', onClick)
          link.removeEventListener('keydown', onKey)
        })
      })

      // Honour whichever tab the capture marked current.
      const initial = links.findIndex((l) => l.classList.contains('w--current'))
      select(initial === -1 ? 0 : initial)
    }

    return () => {
      for (const off of cleanups) off()
    }
  }, [route])
}
